import * as React from 'react'

const TOAST_LIMIT = 5
const TOAST_AUTO_DISMISS_DELAY = 5000
const TOAST_REMOVE_DELAY = 5000

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST' as const,
  UPDATE_TOAST: 'UPDATE_TOAST' as const,
  DISMISS_TOAST: 'DISMISS_TOAST' as const,
  REMOVE_TOAST: 'REMOVE_TOAST' as const,
}

export type ToasterToast = {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  theme?: 'default' | 'destructive' | 'success'
  className?: string
  // Makes the whole toast clickable; pair it with cursor-pointer.
  onClick?: () => void
  // Infinity keeps the toast up until the user dismisses it. The value also
  // reaches the Radix root, which skips its own timer on Infinity.
  duration?: number
}

type Action =
  | { type: 'ADD_TOAST'; toast: ToasterToast }
  | {
      type: 'UPDATE_TOAST'
      toast: Partial<ToasterToast> & Pick<ToasterToast, 'id'>
    }
  | { type: 'DISMISS_TOAST'; toastId?: string }
  | { type: 'REMOVE_TOAST'; toastId?: string }

type State = {
  toasts: ToasterToast[]
}

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

// Radix pauses its own dismiss timer whenever the window is unfocused, so drive
// the auto-close from the store instead. Hovering a toast pauses this timer and
// leaving resumes it with the time it had left.
type DismissTimer = {
  handle: ReturnType<typeof setTimeout>
  startedAt: number
  remaining: number
}

const dismissTimers = new Map<string, DismissTimer>()

const clearDismissTimer = (toastId: string) => {
  const timer = dismissTimers.get(toastId)
  if (timer) {
    clearTimeout(timer.handle)
    dismissTimers.delete(toastId)
  }
}

const startDismissTimer = (toastId: string, remaining: number) => {
  clearDismissTimer(toastId)
  const handle = setTimeout(() => {
    dismissTimers.delete(toastId)
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId })
  }, remaining)
  dismissTimers.set(toastId, { handle, startedAt: Date.now(), remaining })
}

export const pauseToastDismiss = (toastId: string) => {
  const timer = dismissTimers.get(toastId)
  if (!timer) return
  clearTimeout(timer.handle)
  timer.remaining -= Date.now() - timer.startedAt
}

export const resumeToastDismiss = (toastId: string) => {
  const timer = dismissTimers.get(toastId)
  if (!timer) return
  startDismissTimer(toastId, Math.max(timer.remaining, 0))
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      }
    }
    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, 'id'>

function toast({ duration = TOAST_AUTO_DISMISS_DELAY, ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id },
    })
  const dismiss = () => {
    clearDismissTimer(id)
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id })
  }

  if (Number.isFinite(duration)) startDismissTimer(id, duration)

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      duration,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    ...state,
    toast,

    successToast: (title: string, description?: string) =>
      toast({ title, description, theme: 'success' }),

    errorToast: (title: string, error: Error | string) => {
      if (typeof error === 'string') {
        error = new Error(error)
      }
      toast({ title, description: error.message, theme: 'destructive' })
    },

    dismiss: (toastId?: string) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  }
}

export { toast, useToast }
